import React, { useEffect, useMemo, useState } from "react";
import { api } from "../utils/api";

type SlotDay = {
  date: string;
  slots: string[];
};

type Props = {
  onDateTimeSelected?: (payload: { date: string; time: string } | null) => void;
};

async function fetchAvailableSlots(): Promise<SlotDay[]> {
  return api.get<SlotDay[]>("/api/user/bookings/available-slots");
}

const formatDateLabel = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    weekday: "short",
  });
};

const AvailableSlots: React.FC<Props> = ({ onDateTimeSelected }) => {
  const [days, setDays] = useState<SlotDay[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<{
    date: string;
    time: string;
  } | null>(null);

  useEffect(() => {
    let mounted = true;
    fetchAvailableSlots()
      .then((data) => {
        if (mounted) {
          setDays(data);
          setLoading(false);
        }
      })
      .catch(() => {
        if (mounted) {
          setDays([]);
          setLoading(false);
        }
      });
    return () => {
      mounted = false;
    };
  }, []);

  const selectSlot = (date: string, time: string) => {
    const payload = { date, time };
    setSelected(payload);
    onDateTimeSelected?.(payload);
  };

  const twoWeeksDays = useMemo(() => days, [days]);

  return (
    <div className="fp-available-slots">
      <h2>Available slots</h2>
      {loading && <p className="muted">Loading next 2 weeks...</p>}
      {!loading && twoWeeksDays.length === 0 && (
        <p className="muted">No available slots right now.</p>
      )}
      <div className="fp-slots-grid">
        {twoWeeksDays.map((day) => (
          <div key={day.date} className="fp-slot-day">
            <div className="fp-slot-date">{formatDateLabel(day.date)}</div>
            <div className="fp-slot-times">
              {day.slots.map((t) => {
                const isSelected =
                  selected?.date === day.date && selected?.time === t;
                return (
                  <button
                    key={t}
                    className={"fp-slot-btn" + (isSelected ? " selected" : "")}
                    onClick={() => selectSlot(day.date, t)}
                    aria-label={`Select ${day.date} at ${t}`}
                    title={`${day.date} ${t}`}
                  >
                    {t}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AvailableSlots;
